import React, {Suspense, useMemo} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate, useSearchParams} from 'react-router-dom';
import clsx from 'clsx';
import {diApi} from '@di-shared/api/di';
import type {ApiResponseTripTagListResponse, TripTagInfoResponse} from '@di-shared/api/di/__generated';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {
  createHistoryFilterSearch,
  createHistoryFilterSearchParams,
  getDefaultHistoryFilterState,
  getHistoryFilterPeriodState,
  HISTORY_FILTER_PERIOD_KR,
  type HistoryFilterPeriodKr,
  type HistoryFilterQueryStateKr,
  parseHistoryFilterSearchParams,
  PERIOD_KR,
} from '@di-shared/features/history/filter/historyFilterQuery.kr';
import HistoryLock from '@di-shared/features/history/setting/HistoryLock';
import {ROUTE_PATHS} from '@di-shared/routes';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import {addDate, endDateOfMonth, isSameYearAndMonth, startDateOfMonth, subDate} from '@shared/dateUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

interface FilteredDateHistoryFilterKrProps {
  vin: string;
}

interface FilterModeContentProps {
  vin: string;
  filterState: HistoryFilterQueryStateKr;
  replaceFilterState(partialState: Partial<HistoryFilterQueryStateKr>): void;
}

interface HistoryFilterTagsSectionProps {
  selectedTagIds: Array<string>;
  onChangeTagIds(tagIds: Array<string>): void;
}

export default function ({vin}: FilteredDateHistoryFilterKrProps) {
  return (
    <ErrorBoundary fallback={<FallbackError />}>
      <Suspense fallback={<FallbackLoading />}>
        <HistoryLock
          vin={vin}
          fallbackLoading={<FallbackLoading />}
          fallbackError={<FallbackError />}
        >
          <Inner vin={vin} />
        </HistoryLock>
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin}: {vin: string}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterState = useMemo(() => parseHistoryFilterSearchParams(searchParams), [searchParams]);

  function replaceFilterState(partialState: Partial<HistoryFilterQueryStateKr>) {
    setSearchParams(createHistoryFilterSearchParams({...filterState, ...partialState}), {replace: true});
  }

  return (
    <InnerFilterMode
      vin={vin}
      filterState={filterState}
      replaceFilterState={replaceFilterState}
    />
  );
}

function InnerFilterMode({vin, filterState, replaceFilterState}: FilterModeContentProps) {
  const navigate = useNavigate();
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  function updatePeriod(period: HistoryFilterPeriodKr) {
    replaceFilterState(getHistoryFilterPeriodState(period));
  }

  async function openSelectMonthBottomSheet() {
    const months = Array.from({length: PERIOD_KR.SELECT_MONTH}).map((_, index) => subDate(today, {months: index}));
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date>(ROUTE_PATHS.BOTTOM_SHEET_HISTORY_SELECT_MONTH.getFullPath(), {
      months,
      selectedDate: filterState.period === HISTORY_FILTER_PERIOD_KR.SELECT_MONTH ? filterState.startDate : undefined,
    });

    if (!date) {
      return;
    }

    replaceFilterState({
      period: HISTORY_FILTER_PERIOD_KR.SELECT_MONTH,
      startDate: startDateOfMonth(date),
      endDate: isSameYearAndMonth(date, today) ? today : endDateOfMonth(date),
    });
  }

  async function openStartDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: filterState.startDate,
        minDate: filterState.endDate
          ? subDate(filterState.endDate, {days: PERIOD_KR.RECENT_90_DAYS - 1})
          : startDateOfMonth(subDate(today, {months: PERIOD_KR.SELECT_MONTH - 1})),
        maxDate: filterState.endDate ? filterState.endDate : today,
      }
    );

    if (!date) {
      return;
    }

    replaceFilterState({
      period: HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS,
      startDate: date,
    });
  }

  async function openEndDatePicker() {
    const date = await ccWebviewUtils.openBottomSheetWebViewUrl<Date | null>(
      ROUTE_PATHS.BOTTOM_SHEET_HISTORY_DOWNLOAD_DATEPICKER.getFullPath(),
      {
        selectedDate: filterState.endDate,
        minDate: filterState.startDate ? filterState.startDate : startDateOfMonth(subDate(today, {months: PERIOD_KR.SELECT_MONTH - 1})),
        maxDate:
          filterState.startDate && filterState.startDate < subDate(today, {days: PERIOD_KR.RECENT_90_DAYS - 1})
            ? addDate(filterState.startDate, {days: PERIOD_KR.RECENT_90_DAYS - 1})
            : today,
      }
    );

    if (!date) {
      return;
    }

    replaceFilterState({
      period: HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS,
      endDate: date,
    });
  }

  function handleApply() {
    navigate(
      {
        pathname: ROUTE_PATHS.HISTORY_FILTERED_RESULT.getFullPath(vin),
        search: createHistoryFilterSearch(filterState),
      },
      {replace: true}
    );
  }

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T20414')}</span>
        </h1>
      </Header>
      <main className="content page_my_trips">
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('T20414')}</h2>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10096')}</h3>
            <button
              className="btn_select"
              type="button"
              onClick={openSelectMonthBottomSheet}
            >
              {filterState.period !== HISTORY_FILTER_PERIOD_KR.SELECT_MONTH ? (
                <span className="select_placeholder">{t('T10192')}</span>
              ) : (
                <span className="select_value">{CldrDatetimeFormatter.format(filterState.startDate ? filterState.startDate : today, 'yMMM')}</span>
              )}
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10097')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': filterState.period === HISTORY_FILTER_PERIOD_KR.RECENT_30_DAYS})}
                    type="button"
                    onClick={() => updatePeriod(HISTORY_FILTER_PERIOD_KR.RECENT_30_DAYS)}
                  >
                    {t('T30082_$days', {days: PERIOD_KR.RECENT_30_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': filterState.period === HISTORY_FILTER_PERIOD_KR.RECENT_90_DAYS})}
                    type="button"
                    onClick={() => updatePeriod(HISTORY_FILTER_PERIOD_KR.RECENT_90_DAYS)}
                  >
                    {t('T30082_$days', {days: PERIOD_KR.RECENT_90_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className={clsx('btn btn_filled_tonal', {'is-selected': filterState.period === HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS})}
                    type="button"
                    onClick={() => updatePeriod(HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS)}
                  >
                    {t('T10165')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: filterState.period !== HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={filterState.period !== HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS}
                    onClick={() => openStartDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {filterState.startDate ? CldrDatetimeFormatter.format(filterState.startDate, 'medium') : t('T10166')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className={clsx('btn btn_full btn_outline btn_date', {
                      type_placeholder: filterState.period !== HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS,
                    })}
                    type="button"
                    disabled={filterState.period !== HISTORY_FILTER_PERIOD_KR.CUSTOM_DAYS}
                    onClick={() => openEndDatePicker()}
                  >
                    <span className="body_large_r text_date">
                      {filterState.endDate ? CldrDatetimeFormatter.format(filterState.endDate, 'medium') : t('T10167')}
                    </span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <HistoryFilterTagsSection
          selectedTagIds={filterState.tagIds}
          onChangeTagIds={(tagIds) => replaceFilterState({tagIds})}
        />
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={() => replaceFilterState({...getDefaultHistoryFilterState(), sourceDate: filterState.sourceDate})}
          >
            {t('T10355')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={!filterState.startDate || !filterState.endDate}
            onClick={handleApply}
          >
            {t('T10169')}
          </button>
        </div>
      </div>
    </div>
  );
}

function HistoryFilterTagsSection(props: HistoryFilterTagsSectionProps) {
  return (
    <section className="section">
      <ErrorBoundary fallback={<HistoryFilterTagsSectionErrorFallback />}>
        <Suspense fallback={<HistoryFilterTagsSectionLoading />}>
          <HistoryFilterTagsSectionInner {...props} />
        </Suspense>
      </ErrorBoundary>
    </section>
  );
}

function HistoryFilterTagsSectionInner({selectedTagIds, onChangeTagIds}: HistoryFilterTagsSectionProps) {
  const {t} = useInferableTranslation();

  const {data: tripTags} = useSuspenseQuery<ApiResponseTripTagListResponse, Error, Array<TripTagInfoResponse>>({
    queryKey: [getQueryKey(diApi.tripTags.getAllTags)],
    queryFn: () => diApi.tripTags.getAllTags(),
    select: ({data}) => data.tags,
  });

  const isAllTagsSelected = selectedTagIds.length === 0;

  function toggleTagSelection(tagId: string) {
    if (selectedTagIds.length === 0) {
      onChangeTagIds([tagId]);
      return;
    }

    if (selectedTagIds.includes(tagId)) {
      const nextTagIds = selectedTagIds.filter((id) => id !== tagId);

      onChangeTagIds(nextTagIds.length > 0 ? nextTagIds : []);
      return;
    }

    onChangeTagIds([...selectedTagIds, tagId]);
  }

  return (
    <>
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('T10354')}</h3>
      </div>
      <div className="tag_area">
        <button
          type="button"
          className={clsx('btn btn_round', {'is-selected': isAllTagsSelected})}
          onClick={() => onChangeTagIds([])}
        >
          <span
            className="ic ic_check_16px"
            aria-hidden="true"
          ></span>
          {t('T30587')}
        </button>
        {tripTags.map(({tagId, tagName}) => (
          <button
            key={tagId}
            type="button"
            className={clsx('btn btn_round', {'is-selected': selectedTagIds.includes(tagId)})}
            onClick={() => toggleTagSelection(tagId)}
          >
            <span
              className="ic ic_check_16px"
              aria-hidden="true"
            ></span>
            {tagName}
          </button>
        ))}
      </div>
    </>
  );
}

function HistoryFilterTagsSectionLoading() {
  const {t} = useInferableTranslation();

  return (
    <>
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('T10354')}</h3>
      </div>
      <div className="tag_area">
        {Array.from({length: 4}).map((_, index) => (
          <span
            key={`filtered-history-filter-tag-skeleton-${index}`}
            className="btn btn_round skeleton"
            style={{width: 72, height: 32}}
            aria-hidden="true"
          />
        ))}
      </div>
    </>
  );
}

function HistoryFilterTagsSectionErrorFallback() {
  const {t} = useInferableTranslation();

  return (
    <>
      <div className="section_container_header">
        <h3 className="title_xxsmall">{t('T10354')}</h3>
      </div>
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span
            className="body_small_r"
            style={{whiteSpace: 'pre-wrap'}}
          >
            {t('T10175')}
          </span>
        </div>
      </div>
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T20414')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2
              className="section_heading_title skeleton"
              style={{width: '120px'}}
            >
              {t('T20414')}
            </h2>
          </div>
          <div className="setting_box skeleton">
            <h3 className="title_xxsmall">{t('T10096')}</h3>
            <button
              className="btn_select"
              type="button"
            >
              <span className="select_placeholder">{t('T10192')}</span>
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
          <div className="setting_box skeleton">
            <h3 className="title_xxsmall">{t('T10097')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD_KR.RECENT_30_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD_KR.RECENT_90_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T10165')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10166')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10167')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
        <section className="section">
          <div className="section_container_header">
            <h3 className="title_xxsmall skeleton">{t('T10354')}</h3>
          </div>
          <div className="tag_area">
            {Array.from({length: 4}).map((_, index) => (
              <span
                key={`filtered-history-filter-fallback-tag-skeleton-${index}`}
                className="btn btn_round skeleton"
                style={{width: 72, height: 32}}
                aria-hidden="true"
              />
            ))}
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
          >
            {t('T10355')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
          >
            {t('T10169')}
          </button>
        </div>
      </div>
    </div>
  );
}

function FallbackError() {
  const {t} = useInferableTranslation();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">
          <span className="blind">{t('T20414')}</span>
        </h1>
      </Header>
      <main className="content page_setting">
        <section className="section">
          <div className="section_heading">
            <h2 className="section_heading_title">{t('T20414')}</h2>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10097')}</h3>
            <div className="area_period">
              <ul className="list_period">
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD_KR.RECENT_30_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T30082_$days', {days: PERIOD_KR.RECENT_90_DAYS})}
                  </button>
                </li>
                <li className="list_period_item">
                  <button
                    className="btn btn_filled_tonal"
                    type="button"
                  >
                    {t('T10165')}
                  </button>
                </li>
              </ul>
              <div className="select_date_box">
                <div className="btn_box">
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10166')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                  <span
                    className="body_large_b tilde"
                    role="none"
                  >
                    -
                  </span>
                  <button
                    className="btn btn_full btn_outline btn_date"
                    type="button"
                  >
                    <span className="body_large_r text_date">{t('T10167')}</span>
                    <span className="ic ic_calendar_20px" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="setting_box">
            <h3 className="title_xxsmall">{t('T10096')}</h3>
            <button
              className="btn_select"
              type="button"
            >
              <span className="select_placeholder">{t('T10192')}</span>
              <span
                className="ic ic_arrow_down_16px"
                aria-hidden="true"
              />
            </button>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
          >
            {t('T10355')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
          >
            {t('T10169')}
          </button>
        </div>
      </div>
    </div>
  );
}
